import {
  PriceSummary,
  PriceSummaryTotalRow,
} from "@ps-design-system/components/PriceSummary";
import { Text } from "@ps-design-system/components/Text";
import PropTypes from "prop-types";
import React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { useCheckoutContext } from "../../CheckoutContextProvider";
import { getCurrencyFromListing } from "../../checkout-utils";
import { AddOnsLineItem } from "./AddOnsLineItem";
import { AddtlChargesLineItems } from "./AddtlChargesLineItems";
import { CreditsLineItem } from "./CreditsLineItem";
import { DiscountLineItems } from "./DiscountLineItems";
import { HourlyRateLineItem } from "./HourlyRateLineItem";
import { ProcessingLineItem } from "./ProcessingLineItem";
import { SalesTaxLineItem } from "./SalesTaxLineItem";
import { SplitPaymentFeeLineItem } from "./SplitPaymentFeeLineItem";

export const BookingRequestPriceSummary = ({
  urlPrefix,
  accessToken,
  SSOid,
  showSkeleton,
  showTotalBorder = true,
  tooltipAlign = "center",
}) => {
  const { quote, paymentForm, listing } = useCheckoutContext();
  const { masterContract, spaceRentalContract } = quote || {};
  const { booking } = spaceRentalContract?.meta || {};
  const currency = getCurrencyFromListing(listing);

  const splitPaymentObj =
    masterContract?.calculations?.guest?.split_payment || {};
  const splitPaymentSelected =
    paymentForm?.watch("selectedPaymentOption") === "pay_partial";

  return (
    <PriceSummary
      testId="booking-request-price-summary"
      textSize="large"
      showSkeleton={showSkeleton}
    >
      <HourlyRateLineItem
        booking={booking}
        currency={currency}
        accessToken={accessToken}
        listingId={listing?._id}
        tooltipAlign={tooltipAlign}
      />

      {!!booking?.discounts?.length && (
        <DiscountLineItems discounts={booking.discounts} currency={currency} />
      )}

      <AddtlChargesLineItems
        addtlCharges={booking?.addtl_charges}
        currency={currency}
        tooltipAlign={tooltipAlign}
      />

      <ProcessingLineItem
        processingFee={masterContract?.calculations?.guest?.fee || 0}
        currency={currency}
        leanMoreLink={`https://support.peerspace.com/hc${urlPrefix}/articles/214284346`}
        tooltipAlign={tooltipAlign}
      />

      {masterContract?.calculations?.guest?.us_sales_tax_space_rental > 0 && (
        <SalesTaxLineItem
          salesTaxTotal={
            masterContract?.calculations?.guest?.us_sales_tax_space_rental
          }
          currency={currency}
        />
      )}

      {splitPaymentSelected && (
        <SplitPaymentFeeLineItem
          splitPaymentFee={splitPaymentObj?.fee}
          currency={currency}
        />
      )}

      {!!quote?.addOnsContracts?.length && (
        <AddOnsLineItem
          currency={currency}
          SSOid={SSOid}
          tooltipAlign={tooltipAlign}
        />
      )}

      {quote?.guestPaymentDetails?.subtotal_ps_credit > 0 && (
        <CreditsLineItem
          currency={currency}
          totalCredits={quote?.guestPaymentDetails?.subtotal_ps_credit}
        />
      )}

      <PriceSummaryTotalRow
        style={!showTotalBorder ? { borderTop: "none" } : {}}
        name={
          <FormattedMessage id="price_summary.total" defaultMessage="Total" />
        }
        value={
          !isNaN(+quote?.guestPaymentDetails?.subtotal_remaining) ? (
            <FormattedNumber
              value={quote?.guestPaymentDetails?.subtotal_remaining}
              style="currency"
              currency={currency}
            />
          ) : (
            <FormattedMessage
              id="price_summary.unavailable"
              defaultMessage="Unavailable"
            />
          )
        }
      />

      {!quote?.masterContract && (
        <div
          style={{
            marginTop: "8px",
            backgroundColor: "#F7F3EB",
            padding: "16px",
            borderRadius: "4px",
          }}
        >
          <Text typeface="body-1-m-long" style={{ color: "#886A2F" }}>
            <FormattedMessage
              id="price_summary.select_date_time"
              defaultMessage="Please enter a date, start time, and end time to see the total."
            />
          </Text>
        </div>
      )}
    </PriceSummary>
  );
};

BookingRequestPriceSummary.displayName = "BookingRequestPriceSummary";
BookingRequestPriceSummary.propTypes = {
  urlPrefix: PropTypes.string,
  SSOid: PropTypes.string,
  showSkeleton: PropTypes.bool,
  showTotalBorder: PropTypes.bool,
  accessToken: PropTypes.string,
  tooltipAlign: PropTypes.string,
};
